/* eslint-disable no-console */
import { GlobalState } from '@leverege/ui-redux';
import { Toast } from '@leverege/ui-elements';
import { Constants } from '@leverege/api-redux';
import P from '@leverege/promise';
import Molten from '@leverege/molten';
import { SKU_BLUEPRINT_MAP } from '../constants';
const transform = {
  'Position can only be requested from TR_IDLE or LM_IDLE state.': () => {
    return 'Position can only be requested when the Locator State is unpaired. ' + 'If the Locator Status is unpaired, check that the State Change Status is up to date.';
  }
};
const transformers = [];
function transformErrorMessage(msg, context) {
  let fn;
  if (transform[msg]) {
    fn = transform[msg];
  } else {
    fn = transformers.find(t => t.handles(msg, context));
  }
  if (fn) {
    return fn(msg, context);
  }
  return msg;
}

/**
 *
 * @param {object} sale
 * @param {object} options
 * @param {object} options.actions
 * @param {function} options.onChange
 * @param {function} options.onClose
 * @param {boolean} options.throwErr
 */
async function reconcileSaleLocator(options) {
  const {
    sale,
    actions,
    throwErr = false,
    toast = true,
    onChange,
    onClose
  } = options;
  const {
    locationId,
    companyId
  } = actions?.match?.params || {};
  const saleId = sale?.id;
  onChange?.({
    data: 'loading',
    value: true
  });
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('sales').obj(saleId).srv('recovr');
      }
      await nApi.post('reconcileLocator', {
        locationId
      });
      if (toast) {
        Toast.success('Sale Locator Reconciliation Requested Successfully');
      }
    } catch (err) {
      console.error(err);
      if (toast) {
        Toast.error(['Error Requesting Sale Locator Reconciliation', transformErrorMessage(err?.message)]);
      }
      if (throwErr) {
        throw err;
      }
    }
    onClose?.();
  });
}

/**
 *
 * @param {object} options
 * @param {object} options.locator
 * @param {object} options.actions
 * @param {function} options.onChange
 * @param {function} options.onClose
 * @param {boolean} options.throwErr
 */
async function reconcileLocator(options) {
  const {
    locator,
    actions,
    throwErr = false,
    toast = true,
    onChange,
    onClose
  } = options;
  let {
    locationId,
    companyId
  } = actions?.match?.params || {};
  locationId = locationId ?? locator.data?.data?.location?.id;
  companyId = companyId ?? locator.data?.data?.company?.id;
  const locatorId = locator?.id;
  onChange?.({
    data: 'loading',
    value: true
  });
  await GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('locators').obj(locatorId).srv('recovr');
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('locators').obj(locatorId).srv('recovr');
      }
      await nApi.post('reconcile', {
        locationId
      });
      if (toast) {
        Toast.success('Locator Position Requested Successfully');
      }
    } catch (err) {
      console.error(err);
      if (toast) {
        Toast.error(['Error Requesting Locator Position', transformErrorMessage(err?.message)]);
      }
      if (throwErr) {
        throw err;
      }
    }
    onClose?.();
  });
}

// checks permission to reconcile Lost blueprint device and locator
async function canReconcileLost(options) {
  const {
    lost,
    locationId,
    companyId
  } = options;
  const lostId = lost?.id;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      let nApi;
      if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('lost').obj(lostId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('lost').obj(lostId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('lost').obj(lostId);
      }
      const res = await nApi.srv('recovr').post('reconcileLost', null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: lostId,
        action: 'reconcileLost',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error({
        err
      }, 'Error checking reconciling lost locator permission');
      return false;
    }
  });
}

// checks permission to reconcile Lost blueprint device and locator
async function canReconcileJob(options) {
  const {
    job
  } = options;
  const type = SKU_BLUEPRINT_MAP[job.data?.data?.sku];
  const server = type === 'locator' || type === 'beacon' ? 'recovrLogUpload' : 'kudelskidevice';
  const systemId = Molten.getConfig('api/systemId');
  const jobId = job.data?.data?.jobId;
  return GlobalState.dispatch(async (dispatch, getState, {
    api
  }) => {
    try {
      const nApi = api.system(systemId);
      const res = await nApi.srv(server).post(`job/${jobId}/reconcile`, null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: job.id,
        action: 'reconcile',
        allowed: res.allowed === true
      });
      return res.allowed;
    } catch (err) {
      console.error({
        err
      }, 'Error checking reconciling job permission');
      return false;
    }
  });
}
async function reconcileJobs(options) {
  const {
    jobs
  } = options;
  try {
    const successes = [];
    const fails = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      await P.mapLimit(jobs, async job => {
        const type = SKU_BLUEPRINT_MAP[job.data?.data?.sku];
        const server = type === 'locator' || type === 'beacon' ? 'recovrLogUpload' : 'kudelskidevice';
        const systemId = Molten.getConfig('api/systemId');
        const jobId = job.data?.data?.jobId;
        try {
          const nApi = api.system(systemId);
          const res = await nApi.srv(server).post(`job/${jobId}/reconcile`);
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            successes.push(res);
          } else {
            fails.push(res);
            Toast.error(`Could not reconcile Job device ${jobId}`);
          }
        } catch (err) {
          if (err.message === 'JSON.parse: unexpected end of data at line 1 column 1 of the JSON data') {
            successes.push(err);
            return;
          }
          console.error(`Error reconciling Job device ${jobId}: ${err.message}`);
          fails.push(err);
          Toast.error(`Could not reconcile Job device: ${err.message}`);
        }
      }, {
        limit: 5
      });
      const successesLength = successes.length;
      const failsLength = fails.length;
      const reconcileTotal = successesLength + failsLength;
      if (successesLength === 1 && failsLength === 0) {
        Toast.success('The reconciliation process has been successfully initiated. It may take some time for it to complete.');
      } else if (successesLength > 0) {
        Toast.success(`The reconcilliation process has been successfully initiated for ${successesLength} out of
          ${reconcileTotal} jobs.  It may take some time for it to complete.`);
      }
    });
  } catch (err) {
    console.error('Could not reconcile jobs', err);
    throw err;
  }
}

// reconciles Lost blueprint device and associated lost locator
async function reconcileLost(options) {
  const {
    losts,
    actions
  } = options;
  const {
    locationId,
    companyId
  } = actions?.match?.params || {};
  try {
    const successes = [];
    const fails = [];
    await GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      await P.mapLimit(losts, async lost => {
        const lostId = lost?.id;
        try {
          let nApi;
          if (companyId && locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('lost').obj(lostId);
          } else if (companyId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('lost').obj(lostId);
          } else if (locationId) {
            nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('lost').obj(lostId);
          } else {
            nApi = api.interface(process.env.SYSTEM_ID, 'lost').obj(lostId);
          }
          const res = await nApi.srv('recovr').post('reconcileLost');
          if (res && !res?.error && (!res.status || res.status === 200 || res.status === 201)) {
            successes.push(res);
          } else {
            fails.push(res);
            Toast.error(`Could not reconcile Lost device ${lostId}`);
          }
        } catch (err) {
          console.error(`Error reconciling Lost device ${lostId}: ${err.message}`);
          fails.push(err);
          Toast.error(`Could not reconcile Lost device: ${err.message}`);
        }
      }, {
        limit: 5
      });
      const successesLength = successes.length;
      const failsLength = fails.length;
      const reconcileTotal = successesLength + failsLength;
      if (successesLength === 1 && failsLength === 0) {
        Toast.success('The reconciliation process has been successfully initiated. It may take some time for it to complete.');
      } else if (successesLength > 0) {
        Toast.success(`The reconcilliation process has been successfully initiated for ${successesLength} out of
          ${reconcileTotal} locators.  It may take some time for it to complete.`);
      }
    });
  } catch (err) {
    console.error('Could not reconcile lost locators', err);
    throw err;
  }
}
export default {
  reconcileLocator,
  reconcileSaleLocator,
  canReconcileLost,
  reconcileLost,
  canReconcileJob,
  reconcileJobs
};